import { Link } from "react-router-dom";
import { useI18n, LANGS } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Aperture } from "lucide-react";

export function Logo({ to = "/" }) {
  const { t } = useI18n();
  return (
    <Link to={to} className="flex items-center gap-2 group" data-testid="brand-logo">
      <span className="h-8 w-8 rounded-lg bg-[var(--brand)] grid place-items-center transition-transform duration-300 group-hover:rotate-45">
        <Aperture className="h-4.5 w-4.5 text-white" size={18} />
      </span>
      <span className="font-display text-lg font-semibold tracking-tight">{t("brand")}</span>
    </Link>
  );
}

export function LangSwitch({ onChange, className = "" }) {
  const { lang, setLang } = useI18n();
  const { user, setUser } = useAuth();
  const pick = (l) => {
    if (l === lang) return;
    setLang(l, true);
    if (onChange) onChange(l);
    else if (user) api.patch("/auth/profile", { lang: l }).then((r) => setUser(r.data)).catch(() => {});
  };
  return (
    <div className={`inline-flex rounded-full border border-[var(--line)] p-0.5 text-xs font-semibold ${className}`} data-testid="lang-switch" role="group" aria-label="Language / Dil / Sprache">
      {LANGS.map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => pick(l)}
          aria-pressed={lang === l}
          lang={l}
          title={{ tr: "Türkçe", en: "English", de: "Deutsch" }[l]}
          className={`px-3 py-1 rounded-full transition-colors ${lang === l ? "bg-[var(--brand)] text-white" : "text-neutral-400 hover:text-white"}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export function StatusBadge({ status }) {
  const { t } = useI18n();
  const color = {
    completed: "bg-emerald-500/15 text-emerald-400", failed: "bg-red-500/15 text-red-400", processing: "bg-amber-500/15 text-amber-300",
    queued: "bg-sky-500/15 text-sky-300", canceled: "bg-neutral-500/15 text-neutral-400", idle: "bg-neutral-500/15 text-neutral-400",
  }[status] || "bg-neutral-500/15 text-neutral-400";
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`} data-testid="status-badge">{t(`status.${status}`)}</span>;
}

export function PageHeader({ title, sub, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold" data-testid="page-title">{title}</h1>
        {sub && <p className="text-neutral-400 mt-1 text-sm">{sub}</p>}
      </div>
      {children}
    </div>
  );
}

export function Empty({ icon: Icon, text, children, testid = "empty-state" }) {
  return (
    <div className="panel p-10 text-center flex flex-col items-center gap-3" data-testid={testid}>
      {Icon && <Icon className="text-neutral-500" size={28} />}
      <p className="text-neutral-400 text-sm max-w-sm">{text}</p>
      {children}
    </div>
  );
}
